import axios from 'axios'
import * as Sentry from '@sentry/vue'

interface AuthStore {
    logout: Function
    setLoginState: Function
    state: { token: string | null; expiryDate: number }
    hasPermissions: Function
}

export const apiCall = axios.create({
    baseURL: import.meta.env.VITE_API_URL
})

apiCall.interceptors.request.use((config) => {
    // Only set auth header if not already set by the request config
    if (!config.headers.authorization) {
        const authData = localStorage.getItem('auth')
        if (authData) {
            try {
                const auth = JSON.parse(authData)
                if (auth.token) {
                    config.headers.authorization = auth.token
                }
            } catch (e) {
                console.error('Failed to parse auth data:', e)
            }
        }
    }
    return config
})

// Add response interceptor to capture API errors in Sentry
apiCall.interceptors.response.use(
    (response) => response,
    (error) => {
        // Capture API errors in Sentry with rich context
        const isPossibleBlocking =
            error.message?.includes('Network Error') ||
            error.message?.includes('Failed to fetch') ||
            !error.response

        // Don't capture auth errors (handled separately)
        const isAuthError = error.response?.status === 401 || error.response?.status === 403

        // Don't capture 404s for most routes (too noisy)
        const is404 = error.response?.status === 404

        if (!isAuthError && !is404) {
            Sentry.captureException(error, {
                level: error.response?.status >= 500 ? 'error' : 'warning',
                tags: {
                    error_type: 'api_failure',
                    status_code: error.response?.status?.toString(),
                    method: error.config?.method?.toUpperCase(),
                    possible_blocking: isPossibleBlocking ? 'true' : 'false',
                },
                contexts: {
                    api: {
                        url: error.config?.url,
                        baseURL: error.config?.baseURL,
                        method: error.config?.method,
                        status: error.response?.status,
                        statusText: error.response?.statusText,
                        responseData: error.response?.data,
                    },
                    network: {
                        message: error.message,
                        code: error.code,
                        isNetworkError: isPossibleBlocking,
                    },
                },
                fingerprint: [
                    'api-error',
                    error.config?.method || 'unknown',
                    error.response?.status?.toString() || 'network',
                    error.config?.url || 'unknown',
                ],
            })

            // Add breadcrumb for the failed request
            Sentry.addBreadcrumb({
                category: 'api.error',
                message: `API ${error.config?.method?.toUpperCase()} ${error.config?.url} failed`,
                level: 'error',
                data: {
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    url: error.config?.url,
                    method: error.config?.method,
                    possibleBlocking: isPossibleBlocking,
                },
            })
        }

        return Promise.reject(error)
    }
)

export const apiGet = async <T>(path: string, authStore: AuthStore) => {
    try {
        renewToken(authStore)
        const response = await apiCall.get<T>(path, {
            headers: { authorization: authStore.state.token }
        })
        return await response.data
    } catch (error) {
        checkAuth(error, authStore)
        throw error
    }
}

export const apiGetHeaders = async <T>(path: string, token: string) => {
    const response = await apiCall.get<T>(path, {
        headers: { authorization: token }
    })
    return await response.data
}

export const apiPostPublic = async <T>(path: string, body: { [key: string]: any }) => {
    const response = await apiCall.post<T>(path, body)

    return await response
}

const checkAuth = (error: any, authStore: AuthStore) => {
    if (error?.message?.includes('401') || error?.message?.includes('403')) {
        // Get the current path for redirect
        const currentPath = window.location.pathname + window.location.search

        // Pass redirect parameter to logout function
        authStore.logout(currentPath)
    }
}

export const apiPost = async <T>(
    path: string,
    body: { [key: string]: any },
    authStore: AuthStore
) => {
    try {
        renewToken(authStore)
        const response = await apiCall.post<T>(path, body, {
            headers: { authorization: authStore.state.token }
        })

        return await response.data
    } catch (error) {
        checkAuth(error, authStore)
        throw error
    }
}

const renewToken = async (authStore: AuthStore) => {
    if (new Date(authStore.state.expiryDate).valueOf() - new Date().valueOf() / 1000 < 60 * 60) {
        try {
            const response = await apiCall.post(
                '/auth/login',
                {},
                { headers: { authorization: authStore.state.token } }
            )
            // Extract the token from the headers
            const token = response.headers['Authorization'] || response.headers['authorization']
            if (token && response.data) {
                // Update the state with the token and user details from the data part of the payload
                authStore.setLoginState(token, response.data.data)
            }
        } catch (error) {
            // Token renewal failed, but we can continue with the current token
            console.warn('Token renewal failed, continuing with current token')
        }
    }
}

export const apiPut = async <T>(
    path: string,
    body: { [key: string]: any },
    authStore: AuthStore
) => {
    try {
        renewToken(authStore)
        const response = await apiCall.put<T>(path, body, {
            headers: { authorization: authStore.state.token }
        })

        return await response.data
    } catch (error) {
        checkAuth(error, authStore)
        throw error
    }
}

export const apiPatch = async <T>(
    path: string,
    body: { [key: string]: any },
    authStore: AuthStore
) => {
    try {
        renewToken(authStore)
        const response = await apiCall.patch<T>(path, body, {
            headers: { authorization: authStore.state.token }
        })

        return await response.data
    } catch (error) {
        checkAuth(error, authStore)
        throw error
    }
}

export const apiDelete = async <T>(path: string, authStore: AuthStore) => {
    try {
        renewToken(authStore)
        const response = await apiCall.delete<T>(path, {
            headers: { authorization: authStore.state.token }
        })

        return await response.data
    } catch (error) {
        checkAuth(error, authStore)
        throw error
    }
}

export const apiPostFormData = async <T>(
    path: string,
    formData: FormData,
    authStore: AuthStore
) => {
    try {
        renewToken(authStore)
        const response = await apiCall.post<T>(path, formData, {
            headers: {
                authorization: authStore.state.token,
                'Content-Type': 'multipart/form-data'
            }
        })

        return await response.data
    } catch (error) {
        checkAuth(error, authStore)
        throw error
    }
}
